import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type JSX,
  type ReactNode,
  type RefObject,
} from 'react';
import type { Asset, Booking, Contact, Ticket, WorkOrder } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import {
  Button,
  IconScrewdriverWrench,
  Input,
  Modal,
  ScreenLayout,
  ViewHost,
  readPref,
  usePendingAppAction,
  usePref,
  useScreen,
} from '@taki/ui';
import type { Column, ScreenAction, ScreenFilterDef, ViewBinding } from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  MAINTENANCE_APP_ID,
  MAINTENANCE_VIEW_TYPES,
  TICKETS_SCREEN_ID,
  WORK_ORDERS_SCREEN_ID,
} from './views.js';
import {
  ASSET_CATEGORY_OPTIONS,
  BOOKING_TYPE,
  CAUSE_CLIENT,
  CAUSE_OPTIONS,
  CHANNEL_OPTIONS,
  CHARGE_TO_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
  URGENT_PRIORITIES,
  WORK_ORDER_STATUS_OPTIONS,
  assetCategory,
  assetName,
  bookingLabel,
  bookingOptionLabel,
  bookingProject,
  categoryInitials,
  contactName,
  customBool,
  customText,
  dateFromInput,
  dateToInput,
  formatEuros,
  formatMinutes,
  indexReferences,
  isoToDate,
  isSameMonth,
  labelOf,
  laborCost,
  partLinesOf,
  partsTotal,
  sortBookingsByRecency,
  sumCost,
  totalCost,
  truncate,
} from './data.js';
import {
  DEFAULT_FILTERS,
  DEFAULT_LABOR_RATE,
  DEFAULT_TICKET_FILTER_ID,
  DEFAULT_TICKET_STATS,
  DEFAULT_WORK_ORDER_FILTER_ID,
  DEFAULT_WORK_ORDER_STATS,
  FILTERS_PREF_KEY,
  LABOR_RATE_PREF_KEY,
  STATS_MAX,
  TICKETS_STATS_PREF_KEY,
  TICKET_FILTER_DEFS,
  TICKET_STAT_DEFS,
  WORK_ORDERS_STATS_PREF_KEY,
  WORK_ORDER_FILTER_DEFS,
  WORK_ORDER_STAT_DEFS,
  ticketFilterPrefId,
  workOrderFilterPrefId,
  type TicketFilterId,
  type TicketStatId,
  type WorkOrderFilterId,
  type WorkOrderStatId,
} from './settings-defs.js';

/**
 * Ecran unique de l'app Maintenance : tracer la casse et l'usure du materiel,
 * de la declaration jusqu'a la refacturation.
 *
 * DEUX SECTIONS, DEUX COLLECTIONS (pattern impose) : les SIGNALEMENTS vivent
 * dans la famille `tickets` (type local `panne`), les INTERVENTIONS dans la
 * famille `workOrders` (type local `reparation`). Deux collections = DEUX
 * appels `useScreen`, avec des `screenId` differents (la vue active, la
 * recherche et le curseur de fiche sont persistes par ecran). Les deux hooks
 * sont appeles inconditionnellement ; seul le RENDU choisit lequel monter.
 *
 * LA SECTION APPARTIENT AU SOCLE (`screen.section` / `screen.setSection`,
 * portee par `query.section`, donc par l'adresse `?s=signalements`) : cet ecran
 * n'a plus de `useState` de section, et il ne recharge plus rien a la main.
 * Consequence directe et voulue : la section entre dans un lien partageable, et
 * changer de section REFERME la fiche ouverte, ce que le socle fait tout seul.
 *
 * Particularite d'ici, qu'aucun autre ecran du pack ne rencontre : il y a DEUX
 * etats d'ecran pour UNE seule barre de menu, donc le changement de section
 * leur est donne aux DEUX (voir `selectSection`). Une adresse ne pouvant
 * decrire qu'UN etat d'ecran, le socle attribue la propriete de l'adresse au
 * PREMIER `useScreen` monte (ici les signalements) ; le second vit en memoire
 * et le dit en developpement. Garder la meme section des deux cotes n'est donc
 * pas une precaution d'ecriture d'adresse, c'est ce qui garantit que la section
 * portee par le lien decrit bien ce que l'ecran affiche.
 *
 * L'app LIT aussi trois familles sans jamais y ecrire : les `assets` de type
 * `materiel` (pour nommer le materiel concerne), les `contacts` (pour nommer le
 * client a refacturer) et les `bookings` de type `location` (pour nommer la
 * location d'origine, c'est-a-dire PENDANT QUELLE location le materiel a ete
 * casse). Ces trois listes sont chargees DANS le chargeur de chaque section et
 * posees dans un index de module (`indexReferences`) AVANT que les
 * enregistrements ne soient rendus : les bindings peuvent ainsi nommer un id
 * sans recevoir de contexte React.
 *
 * Les locations appartiennent a l'app Reservations : ici, LECTURE SEULE, jamais
 * un `sdk.bookings.create` ni un `sdk.bookings.update`.
 *
 * Structure imposee par la cage IA (docs/GABARIT-ECRAN.md) :
 * - UN seul `ScreenLayout` a la racine, les `Modal` en freres ;
 * - acces data UNIQUEMENT via `@taki/sdk` (`useTakiApp`), jamais Firebase ;
 * - les etats standard vont au `ViewHost`, jamais au gabarit ;
 * - les formulaires vivent dans `Modal`, jamais en zone 5 ;
 * - un seul lime a la fois : l'action d'ajout declaree `kind: 'add'` ;
 * - les prefs (`app.maintenance.*`) sont LUES ici et EDITEES dans settings.tsx.
 */

/* ------------------------------------------------------------------ */
/* Le taux horaire : une PREF, jamais une donnee en base               */
/* ------------------------------------------------------------------ */

/**
 * Le taux horaire courant, lu au moment du calcul. Les bindings sont declares
 * une fois pour toutes (leurs closures doivent rester stables) : ils lisent
 * donc la pref a l'appel, et l'ecran redemande un chargement quand elle change,
 * pour que la projection reprenne les nouveaux couts.
 */
function currentLaborRate(): number {
  return readPref<number>(LABOR_RATE_PREF_KEY, DEFAULT_LABOR_RATE);
}

/* ------------------------------------------------------------------ */
/* Ce que SONT les donnees : lecture metier des deux familles          */
/* ------------------------------------------------------------------ */

/** Le titre lisible du signalement (le socle n'a pas de champ `title` natif). */
function ticketTitle(ticket: Ticket): string {
  return customText(ticket, 'title') ?? 'Signalement sans titre';
}

/** Le materiel vise par le signalement (la famille ticket n'a pas d'`assetId` natif). */
function ticketAssetId(ticket: Ticket): string | null {
  return customText(ticket, 'assetId');
}

function ticketAssetLabel(ticket: Ticket): string {
  return assetName(ticketAssetId(ticket)) ?? 'Materiel inconnu';
}

function ticketCauseLabel(ticket: Ticket): string {
  return labelOf(CAUSE_OPTIONS, customText(ticket, 'cause'));
}

/** La location d'origine du signalement (`customFields.bookingId`). */
function ticketBookingId(ticket: Ticket): string | null {
  return customText(ticket, 'bookingId');
}

/**
 * La location d'origine, NOMMEE. `null` quand le signalement n'en a pas : les
 * vues affichent alors leur texte d'absence habituel ("Non renseigne").
 * Un id qui ne pointe plus rien se dit franchement plutot que de disparaitre :
 * il y a bien une location d'origine, c'est elle qu'on ne retrouve pas.
 */
function ticketBookingLabel(ticket: Ticket): string | null {
  const bookingId = ticketBookingId(ticket);
  if (bookingId == null) return null;
  return bookingLabel(bookingId) ?? 'Location introuvable';
}

/** Sous-titre de liste : materiel, cause, auteur du signalement, location. */
function ticketSubtitle(ticket: Ticket): string {
  const reporter = customText(ticket, 'reportedBy');
  const project = bookingProject(ticketBookingId(ticket));
  return [
    ticketAssetLabel(ticket),
    ticketCauseLabel(ticket),
    reporter == null ? '' : `signale par ${reporter}`,
    project == null ? '' : `location ${project}`,
  ]
    .filter((part) => part !== '')
    .join(' · ');
}

/** Avatar de ligne : les initiales de la CATEGORIE du materiel concerne. */
function ticketInitials(ticket: Ticket): string {
  return categoryInitials(assetCategory(ticketAssetId(ticket)));
}

function orderAssetLabel(order: WorkOrder): string {
  return assetName(order.assetId ?? null) ?? 'Materiel inconnu';
}

function orderTitle(order: WorkOrder): string {
  return `Intervention sur ${orderAssetLabel(order)}`;
}

/** Sous-titre de liste : diagnostic tronque et cout total de l'intervention. */
function orderSubtitle(order: WorkOrder): string {
  const diagnosis =
    order.diagnosis == null ? 'Diagnostic a etablir' : truncate(order.diagnosis, 70);
  return `${diagnosis} · ${formatEuros(totalCost(order, currentLaborRate()))}`;
}

function orderInitials(order: WorkOrder): string {
  return categoryInitials(assetCategory(order.assetId ?? null));
}

/** L'etat de refacturation, en un mot lisible dans un tableau. */
function billableLabel(order: WorkOrder): string {
  if (!customBool(order, 'billable') || customText(order, 'chargeTo') !== 'client') {
    return 'Interne';
  }
  const invoice = customText(order, 'invoiceRef');
  return invoice == null ? 'A refacturer' : `Facture ${invoice}`;
}

/** Le detail des pieces, mis a plat pour le tri et l'export. */
function partsSummary(order: WorkOrder): string | null {
  const lines = partLinesOf(order);
  if (lines.length === 0) return null;
  return lines.map((line) => `${line.label} x${line.qty}`).join(' ; ');
}

/** Le detail des pieces, ligne par ligne, pour la fiche. */
function renderPartLines(order: WorkOrder): ReactNode {
  const lines = partLinesOf(order);
  if (lines.length === 0) return null;
  return (
    <span className="flex flex-col gap-0.5">
      {lines.map((line, index) => (
        <span key={`${line.label}-${String(index)}`} className="text-sm text-ink-900">
          {`${line.label} x${line.qty} : ${formatEuros(line.qty * line.unitPrice)}`}
        </span>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Les colonnes (constantes de MODULE)                                 */
/* ------------------------------------------------------------------ */

/**
 * `value` sert au tri, a l'export et aux agregats ; `render` n'est que de
 * l'affichage. Une valeur absente vaut `null`, jamais `undefined`.
 * Le statut n'est PAS une colonne : il vient du role `status`, que le tableau
 * rend deja en colonne (libelle donne par `options.statusLabel`).
 */
const TICKET_COLUMNS: Column<Ticket>[] = [
  { key: 'asset', label: 'Materiel', value: (ticket) => ticketAssetLabel(ticket), sortable: true },
  { key: 'cause', label: 'Cause', value: (ticket) => ticketCauseLabel(ticket), sortable: true },
  {
    key: 'priority',
    label: 'Priorite',
    value: (ticket) => labelOf(TICKET_PRIORITY_OPTIONS, ticket.priority),
    sortable: true,
  },
  {
    /* La colonne qui porte la tracabilite : PENDANT QUELLE location le
       materiel a ete casse. Le libelle lisible, jamais l'identifiant. */
    key: 'booking',
    label: "Location d'origine",
    value: (ticket) => ticketBookingLabel(ticket),
    sortable: true,
  },
  { key: 'openedAt', label: 'Ouvert le', value: (ticket) => ticket.createdAt, sortable: true },
  { key: 'due', label: 'Echeance', value: (ticket) => ticket.slaDueAt ?? null, sortable: true },
];

/** La fiche montre tout : c'est la vue la plus detaillee, d'ou des colonnes en plus. */
const TICKET_RECORD_COLUMNS: Column<Ticket>[] = [
  { key: 'asset', label: 'Materiel', value: (ticket) => ticketAssetLabel(ticket) },
  {
    key: 'category',
    label: 'Categorie',
    value: (ticket) => labelOf(ASSET_CATEGORY_OPTIONS, assetCategory(ticketAssetId(ticket))),
  },
  { key: 'cause', label: 'Cause', value: (ticket) => ticketCauseLabel(ticket) },
  {
    key: 'priority',
    label: 'Priorite',
    value: (ticket) => labelOf(TICKET_PRIORITY_OPTIONS, ticket.priority),
  },
  { key: 'channel', label: 'Canal', value: (ticket) => labelOf(CHANNEL_OPTIONS, ticket.channel) },
  {
    key: 'reportedBy',
    label: 'Signale par',
    value: (ticket) => customText(ticket, 'reportedBy'),
  },
  {
    key: 'client',
    label: 'Client responsable',
    value: (ticket) => contactName(ticket.contactId),
  },
  {
    key: 'booking',
    label: "Location d'origine",
    value: (ticket) => ticketBookingLabel(ticket),
  },
  { key: 'openedAt', label: 'Ouvert le', value: (ticket) => ticket.createdAt },
  { key: 'due', label: 'Echeance de remise en service', value: (ticket) => ticket.slaDueAt ?? null },
  {
    key: 'description',
    label: 'Description',
    value: (ticket) => customText(ticket, 'description'),
  },
  {
    key: 'resolution',
    label: 'Resolution',
    value: (ticket) => ticket.resolutionSummary ?? null,
  },
];

const ORDER_COLUMNS: Column<WorkOrder>[] = [
  { key: 'asset', label: 'Materiel', value: (order) => orderAssetLabel(order), sortable: true },
  {
    key: 'diagnosis',
    label: 'Diagnostic',
    value: (order) => order.diagnosis ?? null,
    render: (order) => (order.diagnosis == null ? null : truncate(order.diagnosis, 60)),
    sortable: true,
  },
  {
    key: 'parts',
    label: 'Pieces',
    value: (order) => Math.round(partsTotal(partLinesOf(order))),
    render: (order) => formatEuros(partsTotal(partLinesOf(order))),
    sortable: true,
    agg: 'sum',
  },
  {
    key: 'labor',
    label: "Main d'oeuvre",
    value: (order) => order.laborMinutes ?? 0,
    render: (order) => formatMinutes(order.laborMinutes),
    sortable: true,
  },
  {
    key: 'total',
    label: 'Total',
    value: (order) => Math.round(totalCost(order, currentLaborRate())),
    render: (order) => formatEuros(totalCost(order, currentLaborRate())),
    sortable: true,
    agg: 'sum',
  },
  {
    key: 'billable',
    label: 'Refacturable',
    value: (order) => billableLabel(order),
    sortable: true,
  },
];

const ORDER_RECORD_COLUMNS: Column<WorkOrder>[] = [
  { key: 'asset', label: 'Materiel', value: (order) => orderAssetLabel(order) },
  { key: 'diagnosis', label: 'Diagnostic', value: (order) => order.diagnosis ?? null },
  {
    key: 'partLines',
    label: 'Pieces posees',
    value: (order) => partsSummary(order),
    render: (order) => renderPartLines(order),
  },
  {
    key: 'parts',
    label: 'Total pieces',
    value: (order) => Math.round(partsTotal(partLinesOf(order))),
    render: (order) => formatEuros(partsTotal(partLinesOf(order))),
  },
  {
    key: 'labor',
    label: "Main d'oeuvre",
    value: (order) => order.laborMinutes ?? 0,
    render: (order) =>
      `${formatMinutes(order.laborMinutes)} (${formatEuros(
        laborCost(order.laborMinutes, currentLaborRate()),
      )})`,
  },
  {
    key: 'total',
    label: 'Cout total',
    value: (order) => Math.round(totalCost(order, currentLaborRate())),
    render: (order) => formatEuros(totalCost(order, currentLaborRate())),
  },
  { key: 'billable', label: 'Refacturation', value: (order) => billableLabel(order) },
  {
    key: 'chargeTo',
    label: 'Imputation',
    value: (order) => labelOf(CHARGE_TO_OPTIONS, customText(order, 'chargeTo')),
  },
  { key: 'client', label: 'Client a refacturer', value: (order) => contactName(order.contactId) },
  { key: 'invoice', label: 'Facture', value: (order) => customText(order, 'invoiceRef') },
  {
    key: 'planned',
    label: 'Planifiee le',
    value: (order) => isoToDate(customText(order, 'plannedAt')),
  },
  { key: 'completed', label: 'Terminee le', value: (order) => order.completedAt ?? null },
];

/* ------------------------------------------------------------------ */
/* Les bindings : un par vue proposee                                  */
/* ------------------------------------------------------------------ */

/**
 * Les bindings sont declares au niveau du MODULE (jamais recrees a chaque
 * rendu) ; seule leur INSTANCIATION est memoisee dans l'ecran, parce que le
 * `set()` du kanban a besoin du SDK, qui vient du contexte de l'hote. Le SDK
 * etant cree une fois au boot, ces tableaux ne sont construits qu'une fois.
 *
 * `reload` est une REF vers le rechargement de l'ecran concerne : le socle
 * n'applique son optimisme que sur le role ecrit (`groupKey`), donc sans ce
 * rappel la pastille de statut de la liste, du tableau et de la fiche
 * continuerait d'afficher l'ANCIEN statut apres un depot de carte. Une ref,
 * parce que les bindings sont construits avant que `useScreen` n'existe.
 */
function makeTicketBindings(
  sdk: TakiSdk,
  reload: RefObject<() => void>,
): ViewBinding<Ticket>[] {
  return [
    {
      /* LISTE : reconnaitre le signalement en une ligne. */
      type: 'core.liste',
      roles: {
        title: { get: (ticket) => ticketTitle(ticket) },
        subtitle: { get: (ticket) => ticketSubtitle(ticket), empty: 'Materiel non renseigne' },
        avatar: { get: (ticket) => ticketInitials(ticket) },
        status: {
          get: (ticket) => ticket.status ?? null,
          options: () => TICKET_STATUS_OPTIONS,
        },
      },
    },
    {
      /* TABLEAU : comparer. Sans colonne declaree, l'entree serait grisee. */
      type: 'core.tableau',
      roles: {
        title: { get: (ticket) => ticketTitle(ticket) },
        avatar: { get: (ticket) => ticketInitials(ticket) },
        status: {
          get: (ticket) => ticket.status ?? null,
          options: () => TICKET_STATUS_OPTIONS,
        },
      },
      columns: TICKET_COLUMNS,
      options: { titleLabel: 'Signalement', statusLabel: 'Statut', emptyCell: 'Non renseigne' },
    },
    {
      /* KANBAN : faire avancer un signalement a la souris. `options()` evite les
         colonnes fantomes, `set()` rend la carte deplacable ET ecrit vraiment. */
      type: 'core.kanban',
      roles: {
        title: { get: (ticket) => ticketTitle(ticket) },
        subtitle: { get: (ticket) => ticketSubtitle(ticket), empty: 'Materiel non renseigne' },
        avatar: { get: (ticket) => ticketInitials(ticket) },
        groupKey: {
          get: (ticket) => ticket.status ?? null,
          options: () => TICKET_STATUS_OPTIONS,
          set: async (ticket, value) => {
            await sdk.tickets.update(ticket.id, { status: String(value) });
            // Ecriture faite : toutes les vues et les stats reprennent la
            // meme verite que la carte deposee.
            reload.current();
          },
        },
      },
    },
    {
      /* CALENDRIER : l'echeance de remise en service est une VRAIE date metier
         (le materiel doit etre reparable avant la prochaine location). */
      type: 'core.calendrier',
      roles: {
        title: { get: (ticket) => ticketTitle(ticket) },
        subtitle: { get: (ticket) => ticketSubtitle(ticket), empty: 'Materiel non renseigne' },
        status: {
          get: (ticket) => ticket.status ?? null,
          options: () => TICKET_STATUS_OPTIONS,
        },
        dateStart: { get: (ticket) => ticket.slaDueAt ?? null, label: 'Echeance' },
      },
    },
    {
      /*
       * FICHE : tout le dossier du signalement.
       *
       * NI `groupKey` NI `dateStart` ici, exprès. La fiche rend les roles
       * restants dans une grille aux libelles du socle ("Groupe", "Debut") :
       * un `groupKey` pose sur le statut afficherait "Groupe : En cours" juste
       * sous la pastille de statut, et un `dateStart` pose sur l'echeance
       * l'afficherait sous le libelle "Debut" alors que la colonne "Echeance de
       * remise en service" la dit deja avec le mot du metier. Les deux valeurs
       * sont donc portees par l'en-tete et par les colonnes, une seule fois.
       */
      type: 'core.fiche',
      roles: {
        title: { get: (ticket) => ticketTitle(ticket) },
        subtitle: { get: (ticket) => ticketSubtitle(ticket), empty: 'Materiel non renseigne' },
        avatar: { get: (ticket) => ticketInitials(ticket) },
        status: {
          get: (ticket) => ticket.status ?? null,
          options: () => TICKET_STATUS_OPTIONS,
        },
      },
      columns: TICKET_RECORD_COLUMNS,
    },
  ];
}

function makeOrderBindings(
  sdk: TakiSdk,
  reload: RefObject<() => void>,
): ViewBinding<WorkOrder>[] {
  return [
    {
      /* LISTE : "Intervention sur <materiel>", diagnostic et cout. */
      type: 'core.liste',
      roles: {
        title: { get: (order) => orderTitle(order) },
        subtitle: { get: (order) => orderSubtitle(order) },
        avatar: { get: (order) => orderInitials(order) },
        status: {
          get: (order) => order.status ?? null,
          options: () => WORK_ORDER_STATUS_OPTIONS,
        },
      },
    },
    {
      /* TABLEAU : ce qui coute, et a qui. */
      type: 'core.tableau',
      roles: {
        title: { get: (order) => orderTitle(order) },
        avatar: { get: (order) => orderInitials(order) },
        status: {
          get: (order) => order.status ?? null,
          options: () => WORK_ORDER_STATUS_OPTIONS,
        },
      },
      columns: ORDER_COLUMNS,
      options: { titleLabel: 'Intervention', statusLabel: 'Statut', emptyCell: 'Non renseigne' },
    },
    {
      /* KANBAN : faire avancer l'atelier, avec ecriture reelle. */
      type: 'core.kanban',
      roles: {
        title: { get: (order) => orderTitle(order) },
        subtitle: { get: (order) => orderSubtitle(order) },
        avatar: { get: (order) => orderInitials(order) },
        groupKey: {
          get: (order) => order.status ?? null,
          options: () => WORK_ORDER_STATUS_OPTIONS,
          set: async (order, value) => {
            await sdk.workOrders.update(order.id, { status: String(value) });
            reload.current();
          },
        },
      },
    },
    {
      /* CALENDRIER : la date planifiee vit en chaine ISO dans customFields (les
         customFields n'acceptent que des primitives) : le `get` la remet en Date. */
      type: 'core.calendrier',
      roles: {
        title: { get: (order) => orderTitle(order) },
        subtitle: { get: (order) => orderSubtitle(order) },
        status: {
          get: (order) => order.status ?? null,
          options: () => WORK_ORDER_STATUS_OPTIONS,
        },
        dateStart: { get: (order) => isoToDate(customText(order, 'plannedAt')), label: 'Planifiee' },
        dateEnd: { get: (order) => order.completedAt ?? null, label: 'Terminee' },
      },
    },
    {
      /*
       * FICHE : le detail chiffre, jusqu'a la reference de facture.
       *
       * Meme regle que la fiche des signalements : pas de `groupKey`, pas de
       * `dateStart`, pas de `dateEnd`. Le socle les rendrait en "Groupe",
       * "Debut" et "Fin", en doublon de la pastille de statut et des colonnes
       * "Planifiee le" et "Terminee le", qui portent deja les mots du metier.
       */
      type: 'core.fiche',
      roles: {
        title: { get: (order) => orderTitle(order) },
        subtitle: { get: (order) => orderSubtitle(order) },
        avatar: { get: (order) => orderInitials(order) },
        status: {
          get: (order) => order.status ?? null,
          options: () => WORK_ORDER_STATUS_OPTIONS,
        },
      },
      columns: ORDER_RECORD_COLUMNS,
    },
  ];
}

/* ------------------------------------------------------------------ */
/* Ce que garde chaque pastille                                        */
/* ------------------------------------------------------------------ */

const TICKET_FILTER_MATCH: Record<TicketFilterId, ((ticket: Ticket) => boolean) | undefined> = {
  tous: undefined,
  ouvert: (ticket) => ticket.status === 'ouvert',
  en_cours: (ticket) => ticket.status === 'en_cours',
  resolu: (ticket) => ticket.status === 'resolu',
};

const ORDER_FILTER_MATCH: Record<WorkOrderFilterId, ((order: WorkOrder) => boolean) | undefined> = {
  toutes: undefined,
  a_planifier: (order) => order.status === 'a_planifier',
  en_cours: (order) => order.status === 'en_cours',
  termine: (order) => order.status === 'termine',
};

/* ------------------------------------------------------------------ */
/* Le formulaire d'intervention : les lignes de pieces                 */
/* ------------------------------------------------------------------ */

/** Une ligne de pieces en cours de saisie (les nombres restent du texte). */
interface PartDraft {
  label: string;
  qty: string;
  unitPrice: string;
}

const EMPTY_PART: PartDraft = { label: '', qty: '1', unitPrice: '' };

/** Classes des champs du formulaire, alignees sur l'Input du socle. */
const FIELD_CLASS =
  'h-[3.125rem] w-full rounded-[0.8125rem] border border-border bg-surface-1 px-4 text-sm ' +
  'text-ink-900 font-sans transition-colors hover:border-border-strong ' +
  'focus-visible:outline-none focus-visible:border-accent-border focus-visible:shadow-ring-soft';

const TEXTAREA_CLASS =
  'min-h-[5rem] w-full rounded-[0.8125rem] border border-border bg-surface-1 px-4 py-3 text-sm ' +
  'text-ink-900 font-sans transition-colors placeholder:text-ink-300 hover:border-border-strong ' +
  'focus-visible:outline-none focus-visible:border-accent-border focus-visible:shadow-ring-soft';

const LABEL_CLASS = 'text-xs font-medium text-ink-500';

/* ------------------------------------------------------------------ */
/* L'ecran                                                             */
/* ------------------------------------------------------------------ */

type SectionId = 'signalements' | 'interventions';

/**
 * La section d'ouverture, et le repli de toute valeur inconnue.
 *
 * Elle vaut aussi bien pour `defaultSection` (les deux etats d'ecran partent du
 * meme pied) que pour la lecture : declarer le defaut a un seul endroit evite
 * qu'un lien portant une vieille section n'ouvre l'app sur un menu sans entree
 * active.
 */
const DEFAULT_SECTION: SectionId = 'signalements';

/**
 * Normalise ce qui arrive du gabarit ou de l'adresse. Deux sections seulement,
 * donc un simple aiguillage suffit : pas de transtypage, et une section
 * inconnue (`?s=vieux-lien`) retombe sur les signalements plutot que d'afficher
 * une zone 5 vide sous un menu ou rien n'est allume.
 */
function toSectionId(value: string | null): SectionId {
  return value === 'interventions' ? 'interventions' : DEFAULT_SECTION;
}

export function MaintenanceScreen(): JSX.Element {
  const { sdk, config } = useTakiApp();

  /* ---- Prefs partagees avec settings.tsx (hub Parametres du shell) ---- */

  const [filterVisibility] = usePref<Record<string, boolean>>(FILTERS_PREF_KEY, DEFAULT_FILTERS);
  const [ticketStatIds] = usePref<string[]>(TICKETS_STATS_PREF_KEY, DEFAULT_TICKET_STATS);
  const [orderStatIds] = usePref<string[]>(WORK_ORDERS_STATS_PREF_KEY, DEFAULT_WORK_ORDER_STATS);
  const [laborRate] = usePref<number>(LABOR_RATE_PREF_KEY, DEFAULT_LABOR_RATE);

  /* ---- Les references LUES : le parc, le carnet d'adresses, le planning ---- */

  const [parc, setParc] = useState<Asset[]>([]);
  const [clients, setClients] = useState<Contact[]>([]);
  const [locations, setLocations] = useState<Booking[]>([]);
  /*
   * Les references ont-elles fini d'arriver au moins une fois ? Un raccourci du
   * dock ouvre le formulaire AVANT la fin du premier chargement : sans ce
   * drapeau, la liste deroulante des locations dirait "aucune location au
   * planning" pendant la seconde ou elle charge, ce qui serait faux.
   */
  const [referencesLoaded, setReferencesLoaded] = useState(false);

  /*
   * Le chargeur de chaque section charge AUSSI les references et alimente
   * l'index de module AVANT de rendre ses enregistrements : quand la
   * projection tourne, les ids de materiel, de client et de location ont deja
   * un nom.
   */
  const loadReferences = useCallback(async (): Promise<void> => {
    const [allAssets, allContacts, allBookings] = await Promise.all([
      sdk.assets.list(),
      sdk.contacts.list(),
      // LECTURE SEULE : les locations appartiennent a l'app Reservations.
      sdk.bookings.list(),
    ]);
    // Le parc cinema coexiste avec les assets des autres apps : on ne montre
    // JAMAIS autre chose que du materiel. Meme geste pour les creneaux : seules
    // les locations du pack nous interessent.
    const materiels = allAssets.filter(
      (asset) => asset.type === 'materiel' && asset.archivedAt == null,
    );
    const rentals = sortBookingsByRecency(
      allBookings.filter(
        (booking) => booking.type === BOOKING_TYPE && booking.archivedAt == null,
      ),
    );
    indexReferences(materiels, allContacts, rentals);
    setParc(materiels);
    setClients(allContacts);
    setLocations(rentals);
    setReferencesLoaded(true);
  }, [sdk]);

  /*
   * CES DEUX CHARGEURS NE LISENT PAS `query.section`, et c'est le seul endroit
   * du pack ou ne pas la lire est la bonne reponse.
   *
   * Ailleurs (l'Inventaire), la section DECOUPE une collection unique : le
   * chargeur la lit et ne rend que la tranche demandee. Ici, chaque section EST
   * une famille. Le chargeur des signalements ne saurait pas rendre une
   * intervention, et lui faire rendre une liste vide pendant que l'autre
   * section est affichee ferait tomber a zero le compteur du menu, qui compte
   * le jeu COMPLET de sa collection : un menu dont les compteurs disparaissent
   * quand on clique a cote est un bug. Les deux collections restent donc
   * chargees en permanence, et la section ne choisit que celle qu'on REGARDE.
   *
   * Le socle rappelle quand meme les deux chargeurs a chaque changement de
   * section (elle fait partie de sa cle de chargement). Le cout assume : un
   * clic de menu relit les deux familles ET les trois listes de references,
   * deux fois, une par chargeur. Rien ne clignote pour autant (le socle ne vide
   * pas le jeu precedent, donc pas de squelette), et le gain est qu'aucun ecran
   * ne recharge plus a la main. Le jour ou ca pese, c'est le double appel a
   * `loadReferences` qu'il faudra mutualiser, pas la section qu'il faudra
   * sortir du socle.
   */
  const loadTickets = useCallback(async (): Promise<Ticket[]> => {
    const [rows] = await Promise.all([sdk.tickets.list(), loadReferences()]);
    return rows.filter((ticket) => ticket.type === 'panne');
  }, [sdk, loadReferences]);

  const loadOrders = useCallback(async (): Promise<WorkOrder[]> => {
    const [rows] = await Promise.all([sdk.workOrders.list(), loadReferences()]);
    return rows.filter((order) => order.type === 'reparation');
  }, [sdk, loadReferences]);

  // Le `set()` du kanban recharge apres son ecriture. Les bindings sont
  // construits AVANT `useScreen` : ils joignent le rechargement par une ref,
  // renseignee plus bas (meme geste que les refs de `useScreen`).
  const reloadTicketsRef = useRef<() => void>(() => undefined);
  const reloadOrdersRef = useRef<() => void>(() => undefined);

  const ticketBindings = useMemo(() => makeTicketBindings(sdk, reloadTicketsRef), [sdk]);
  const orderBindings = useMemo(() => makeOrderBindings(sdk, reloadOrdersRef), [sdk]);

  // Pastilles reellement passees au core : les visibles selon la pref (cle
  // absente = visible, seul un `false` explicite masque).
  const ticketFilters = useMemo<ScreenFilterDef<Ticket>[]>(
    () =>
      TICKET_FILTER_DEFS.filter(
        (def) => filterVisibility[ticketFilterPrefId(def.id)] !== false,
      ).map((def) => {
        const match = TICKET_FILTER_MATCH[def.id];
        return match == null
          ? { id: def.id, label: def.label }
          : { id: def.id, label: def.label, match };
      }),
    [filterVisibility],
  );

  const orderFilters = useMemo<ScreenFilterDef<WorkOrder>[]>(
    () =>
      WORK_ORDER_FILTER_DEFS.filter(
        (def) => filterVisibility[workOrderFilterPrefId(def.id)] !== false,
      ).map((def) => {
        const match = ORDER_FILTER_MATCH[def.id];
        return match == null
          ? { id: def.id, label: def.label }
          : { id: def.id, label: def.label, match };
      }),
    [filterVisibility],
  );

  /* ---- L'ETAT D'ECRAN : deux collections, donc DEUX useScreen ---- */

  const tickets = useScreen<Ticket>({
    appId: MAINTENANCE_APP_ID,
    screenId: TICKETS_SCREEN_ID,
    load: loadTickets,
    getId: (ticket) => ticket.id,
    get: (id) => sdk.tickets.get(id),
    types: MAINTENANCE_VIEW_TYPES,
    bindings: ticketBindings,
    filters: ticketFilters,
    defaultFilterId: DEFAULT_TICKET_FILTER_ID,
    defaultView: 'core.liste',
    // Le MEME defaut des deux cotes : les deux etats doivent partir sur la
    // meme section, sinon la premiere adresse ecrite dependrait de l'ordre
    // des effets.
    defaultSection: DEFAULT_SECTION,
  });

  const orders = useScreen<WorkOrder>({
    appId: MAINTENANCE_APP_ID,
    screenId: WORK_ORDERS_SCREEN_ID,
    load: loadOrders,
    getId: (order) => order.id,
    get: (id) => sdk.workOrders.get(id),
    types: MAINTENANCE_VIEW_TYPES,
    bindings: orderBindings,
    filters: orderFilters,
    defaultFilterId: DEFAULT_WORK_ORDER_FILTER_ID,
    defaultView: 'core.liste',
    defaultSection: DEFAULT_SECTION,
  });

  const {
    markRead: markTicketRead,
    reload: reloadTickets,
    setFilter: setTicketFilter,
    setSection: setTicketsSection,
  } = tickets;
  const {
    markRead: markOrderRead,
    reload: reloadOrders,
    setFilter: setOrderFilter,
    setSection: setOrdersSection,
  } = orders;
  // Les bindings (construits une fois) atteignent le rechargement par ici.
  reloadTicketsRef.current = reloadTickets;
  reloadOrdersRef.current = reloadOrders;
  const ticketFilterId = tickets.query.filterId;
  const orderFilterId = orders.query.filterId;

  /*
   * LA SECTION ACTIVE, lue sur le socle. Elle vit dans la requete des deux
   * etats d'ecran, donc dans l'adresse (`?s=interventions`), donc dans un lien
   * qu'on colle a un collegue : c'est elle qui pilote la zone 5, les pastilles,
   * les stats et les actions de la zone 6 (jamais un second ScreenLayout).
   *
   * On la lit sur l'etat des SIGNALEMENTS et pas sur "l'etat actif" : c'est
   * elle qui designe l'actif, la lire dessus tournerait en rond. Les deux
   * etats portent de toute facon la meme valeur, `selectSection` ne parle
   * jamais a un seul.
   */
  const section = toSectionId(tickets.section);

  /**
   * CHANGER DE SECTION : le meme ordre aux DEUX etats d'ecran.
   *
   * Ce ne sont pas deux ecrans juxtaposes, ce sont les deux faces d'un menu.
   * Seul le premier possede l'adresse (voir l'en-tete du fichier) : oublier le
   * second ne casserait donc pas le lien, il ferait pire, il laisserait la
   * moitie non affichee de l'ecran sur une autre section que celle qu'on
   * regarde, avec sa fiche restee ouverte derriere.
   *
   * Le socle fait le reste, et le second effet n'est pas cosmetique : il
   * recharge, et il REFERME la fiche ouverte, des deux cotes. C'est ce qui
   * compte ici, ou les deux sections ne portent meme pas la meme famille : un
   * signalement ouvert n'a rien a faire derriere la liste des interventions, et
   * son id n'a rien a faire dans l'adresse une fois qu'on l'a quitte.
   */
  const selectSection = useCallback(
    (id: SectionId): void => {
      setTicketsSection(id);
      setOrdersSection(id);
    },
    [setTicketsSection, setOrdersSection],
  );

  // Si la pastille ACTIVE vient d'etre masquee dans les reglages, le filtre
  // retombe sur le defaut (jamais de filtre actif invisible).
  useEffect(() => {
    if (
      ticketFilterId != null &&
      ticketFilterId !== DEFAULT_TICKET_FILTER_ID &&
      filterVisibility[ticketFilterPrefId(ticketFilterId)] === false
    ) {
      setTicketFilter(DEFAULT_TICKET_FILTER_ID);
    }
  }, [filterVisibility, ticketFilterId, setTicketFilter]);

  useEffect(() => {
    if (
      orderFilterId != null &&
      orderFilterId !== DEFAULT_WORK_ORDER_FILTER_ID &&
      filterVisibility[workOrderFilterPrefId(orderFilterId)] === false
    ) {
      setOrderFilter(DEFAULT_WORK_ORDER_FILTER_ID);
    }
  }, [filterVisibility, orderFilterId, setOrderFilter]);

  // Le taux horaire a change dans les reglages : les couts projetes sont
  // perimes. On redemande un chargement des interventions pour que la
  // projection reprenne les nouveaux montants.
  const firstRatePass = useRef(true);
  useEffect(() => {
    if (firstRatePass.current) {
      firstRatePass.current = false;
      return;
    }
    reloadOrders();
  }, [laborRate, reloadOrders]);

  /* ---- Donnees derivees : compteurs et statistiques ---- */

  const liveTickets = useMemo(
    () => tickets.records.filter((ticket) => ticket.archivedAt == null),
    [tickets.records],
  );
  const liveOrders = useMemo(
    () => orders.records.filter((order) => order.archivedAt == null),
    [orders.records],
  );

  const ticketStatValues = useMemo<Record<TicketStatId, string>>(() => {
    const open = liveTickets.filter((ticket) => ticket.status === 'ouvert').length;
    const urgent = liveTickets.filter(
      (ticket) => ticket.priority != null && URGENT_PRIORITIES.includes(ticket.priority),
    ).length;
    const client = liveTickets.filter(
      (ticket) => customText(ticket, 'cause') === CAUSE_CLIENT,
    ).length;
    return {
      total: String(liveTickets.length),
      ouverts: String(open),
      urgents: String(urgent),
      'imputables-client': String(client),
    };
  }, [liveTickets]);

  const orderStatValues = useMemo<Record<WorkOrderStatId, string>>(() => {
    const now = new Date();
    const running = liveOrders.filter((order) => order.status === 'en_cours').length;
    // Cout du mois : ce que l'atelier a REELLEMENT consomme sur les
    // interventions terminees dans le mois civil courant.
    const doneThisMonth = liveOrders.filter(
      (order) => order.status === 'termine' && isSameMonth(order.completedAt, now),
    );
    // A refacturer : ce qui est imputable au client et pas encore facture.
    const toInvoice = liveOrders.filter(
      (order) =>
        customBool(order, 'billable') &&
        customText(order, 'chargeTo') === 'client' &&
        customText(order, 'invoiceRef') == null,
    );
    return {
      total: String(liveOrders.length),
      'en-cours': String(running),
      'cout-mois': formatEuros(sumCost(doneThisMonth, laborRate)),
      'a-refacturer': formatEuros(sumCost(toInvoice, laborRate)),
    };
  }, [liveOrders, laborRate]);

  const headerStats = useMemo(() => {
    if (section === 'signalements') {
      return TICKET_STAT_DEFS.filter((def) => ticketStatIds.includes(def.id))
        .slice(0, STATS_MAX)
        .map((def) => ({ value: ticketStatValues[def.id], label: def.label }));
    }
    return WORK_ORDER_STAT_DEFS.filter((def) => orderStatIds.includes(def.id))
      .slice(0, STATS_MAX)
      .map((def) => ({ value: orderStatValues[def.id], label: def.label }));
  }, [section, ticketStatIds, ticketStatValues, orderStatIds, orderStatValues]);

  /* ---- Les formulaires : UN seul modal ouvert a la fois ---- */

  // Un seul state decide quel modal est ouvert : les deux formulaires et le
  // compte rendu d'echec d'une action de la zone 6 (qui, elle, n'a pas de
  // formulaire ou poser son message).
  const [openModal, setOpenModal] = useState<'ticket' | 'intervention' | 'echec' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [working, setWorking] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** Message d'echec d'une action de la zone 6 (ouverture d'intervention, cloture). */
  const [actionError, setActionError] = useState<string | null>(null);

  const [ticketAsset, setTicketAsset] = useState('');
  const [ticketLabel, setTicketLabel] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketCause, setTicketCause] = useState('usure');
  const [ticketPriority, setTicketPriority] = useState('normale');
  const [ticketChannel, setTicketChannel] = useState('retour_materiel');
  const [ticketBooking, setTicketBooking] = useState('');
  const [ticketDue, setTicketDue] = useState('');

  const [orderAsset, setOrderAsset] = useState('');
  const [orderDiagnosis, setOrderDiagnosis] = useState('');
  const [orderParts, setOrderParts] = useState<PartDraft[]>([{ ...EMPTY_PART }]);
  const [orderLabor, setOrderLabor] = useState('60');
  const [orderPlanned, setOrderPlanned] = useState('');
  const [orderBillable, setOrderBillable] = useState(false);
  const [orderChargeTo, setOrderChargeTo] = useState('interne');

  /** Une date par defaut a n jours, au format du champ `type="date"`. */
  const inDays = useCallback((days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return dateToInput(date);
  }, []);

  const openTicketForm = useCallback((): void => {
    setFormError(null);
    setTicketAsset(parc[0]?.id ?? '');
    setTicketLabel('');
    setTicketDescription('');
    setTicketCause('usure');
    setTicketPriority('normale');
    setTicketChannel('retour_materiel');
    setTicketBooking('');
    setTicketDue(inDays(7));
    setOpenModal('ticket');
  }, [parc, inDays]);

  const openOrderForm = useCallback((): void => {
    setFormError(null);
    setOrderAsset(parc[0]?.id ?? '');
    setOrderDiagnosis('');
    setOrderParts([{ ...EMPTY_PART }]);
    setOrderLabor('60');
    setOrderPlanned(inDays(2));
    setOrderBillable(false);
    setOrderChargeTo('interne');
    setOpenModal('intervention');
  }, [parc, inDays]);

  const closeModal = useCallback((): void => {
    setOpenModal(null);
    setFormError(null);
    setActionError(null);
  }, []);

  /*
   * Le parc arrive APRES l'ouverture du formulaire quand celui-ci vient d'un
   * raccourci du dock (le shell navigue, l'ecran se monte, le modal s'ouvre
   * avant la fin du chargement). Sans ce rattrapage, la liste deroulante du
   * materiel resterait vide et la creation serait bloquee sans le dire.
   */
  useEffect(() => {
    const first = parc[0]?.id;
    if (first == null) return;
    if (openModal === 'ticket') setTicketAsset((current) => (current === '' ? first : current));
    if (openModal === 'intervention') setOrderAsset((current) => (current === '' ? first : current));
  }, [parc, openModal]);

  /* ---- Les shortcuts du dock ---- */

  const { actionId: pendingActionId, consume: consumePendingAction } =
    usePendingAppAction(MAINTENANCE_APP_ID);

  useEffect(() => {
    if (pendingActionId == null) return;
    if (pendingActionId === 'new-ticket') {
      // On bascule d'abord sur la bonne section, PUIS on ouvre le modal. La
      // bascule referme aussi la fiche que la session precedente avait laissee
      // ouverte : le raccourci du dock arrive pour CREER, pas pour reprendre.
      selectSection('signalements');
      openTicketForm();
    }
    if (pendingActionId === 'new-intervention') {
      selectSection('interventions');
      openOrderForm();
    }
    consumePendingAction();
  }, [pendingActionId, consumePendingAction, selectSection, openTicketForm, openOrderForm]);

  /* ---- Ecritures ---- */

  const onCreateTicket = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      setFormError(null);
      setSubmitting(true);
      try {
        const due = dateFromInput(ticketDue);
        // LA CHAINE DE LA REFACTURATION : quand la casse est imputable au
        // client, le client responsable est celui de la location d'origine. On
        // ne l'invente pas, on le lit sur la location choisie ; et on ne le
        // pose que pour la cause `client` (une usure normale n'a personne a
        // qui presenter la facture).
        const booking = locations.find((entry) => entry.id === ticketBooking);
        const responsibleId =
          ticketCause === CAUSE_CLIENT ? (booking?.contactId ?? null) : null;
        const created = await sdk.tickets.create({
          orgId: config.orgId,
          type: 'panne',
          status: 'ouvert',
          priority: ticketPriority,
          channel: ticketChannel,
          ...(due != null ? { slaDueAt: due } : {}),
          ...(responsibleId != null ? { contactId: responsibleId } : {}),
          customFields: {
            assetId: ticketAsset,
            cause: ticketCause,
            title: ticketLabel.trim(),
            ...(ticketDescription.trim() !== ''
              ? { description: ticketDescription.trim() }
              : {}),
            ...(ticketBooking !== '' ? { bookingId: ticketBooking } : {}),
          },
        });
        // Modele de la boite mail : un message qu'on a envoye soi-meme ne
        // s'affiche pas comme non lu. On marque AVANT le rechargement, sinon
        // le signalement qu'on vient de saisir revient du serveur avec son
        // lisere et sa pastille.
        markTicketRead(created.id);
        reloadTickets();
        closeModal();
      } catch (error) {
        setFormError(
          error instanceof Error ? error.message : 'Echec de la creation du signalement.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      sdk,
      config.orgId,
      ticketAsset,
      ticketLabel,
      ticketDescription,
      ticketCause,
      ticketPriority,
      ticketChannel,
      ticketBooking,
      ticketDue,
      locations,
      markTicketRead,
      reloadTickets,
      closeModal,
    ],
  );

  const onCreateOrder = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      setFormError(null);
      setSubmitting(true);
      try {
        const partLines: Record<string, unknown>[] = orderParts
          .filter((part) => part.label.trim() !== '')
          .map((part) => ({
            label: part.label.trim(),
            qty: Number(part.qty) || 0,
            unitPrice: Number(part.unitPrice) || 0,
          }));
        const minutes = Math.max(0, Math.round(Number(orderLabor) || 0));
        const planned = dateFromInput(orderPlanned) ?? new Date();
        const created = await sdk.workOrders.create({
          orgId: config.orgId,
          type: 'reparation',
          assetId: orderAsset,
          status: 'a_planifier',
          ...(orderDiagnosis.trim() !== '' ? { diagnosis: orderDiagnosis.trim() } : {}),
          ...(partLines.length > 0 ? { partLines } : {}),
          laborMinutes: minutes,
          customFields: {
            plannedAt: planned.toISOString(),
            billable: orderBillable,
            chargeTo: orderChargeTo,
          },
        });
        // Meme raison que pour le signalement : ce qu'on vient de creer
        // soi-meme n'est pas une nouveaute pour soi.
        markOrderRead(created.id);
        reloadOrders();
        closeModal();
      } catch (error) {
        setFormError(
          error instanceof Error ? error.message : "Echec de la creation de l'intervention.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      sdk,
      config.orgId,
      orderAsset,
      orderDiagnosis,
      orderParts,
      orderLabor,
      orderPlanned,
      orderBillable,
      orderChargeTo,
      markOrderRead,
      reloadOrders,
      closeModal,
    ],
  );

  /** Le signalement ouvert en fiche, s'il y en a un. */
  const openTicket = useMemo(
    () => tickets.records.find((ticket) => ticket.id === tickets.openId) ?? null,
    [tickets.records, tickets.openId],
  );

  /** L'intervention ouverte en fiche, s'il y en a une. */
  const openOrder = useMemo(
    () => orders.records.find((order) => order.id === orders.openId) ?? null,
    [orders.records, orders.openId],
  );

  /** Le materiel du signalement ouvert : sans lui, il n'y a pas d'intervention a ouvrir. */
  const openTicketAssetId = openTicket == null ? null : ticketAssetId(openTicket);

  /**
   * Du signalement a l'atelier : on cree l'intervention pre-remplie (materiel,
   * signalement d'origine, client responsable), on passe le signalement en
   * cours, puis on bascule sur la section Interventions.
   *
   * Une ecriture qui echoue n'est jamais silencieuse : le message part dans le
   * modal de compte rendu, sinon le bouton se reactiverait sans que rien ne se
   * soit passe.
   */
  const onOpenIntervention = useCallback(async (): Promise<void> => {
    if (openTicket == null) return;
    const assetId = ticketAssetId(openTicket);
    if (assetId == null) return;
    setWorking(true);
    try {
      const causeIsClient = customText(openTicket, 'cause') === CAUSE_CLIENT;
      const planned = new Date();
      planned.setDate(planned.getDate() + 1);
      const created = await sdk.workOrders.create({
        orgId: config.orgId,
        type: 'reparation',
        assetId,
        ticketId: openTicket.id,
        ...(openTicket.contactId != null ? { contactId: openTicket.contactId } : {}),
        status: 'a_planifier',
        diagnosis: `Ouvert depuis le signalement : ${ticketTitle(openTicket)}`,
        customFields: {
          plannedAt: planned.toISOString(),
          billable: causeIsClient,
          chargeTo: causeIsClient ? 'client' : 'interne',
        },
      });
      if (openTicket.status !== 'en_cours') {
        await sdk.tickets.update(openTicket.id, { status: 'en_cours' });
      }
      // L'intervention vient d'etre ouverte PAR L'UTILISATEUR : elle n'a rien
      // d'une nouveaute pour lui, et la bascule de section l'amene justement
      // dessus. Marquee avant les rechargements, comme les deux creations du
      // formulaire.
      markOrderRead(created.id);
      // Les deux rechargements ET la bascule de section partent dans le meme
      // tour : ils se rangent dans une seule cle de chargement, donc chaque
      // collection ne part chercher ses donnees qu'UNE fois. Les demander
      // explicitement plutot que de compter sur la bascule garde ce geste
      // juste meme le jour ou ce bouton serait ouvrable depuis les
      // interventions (une section qui ne change pas ne recharge rien).
      reloadTickets();
      reloadOrders();
      // La bascule referme au passage le signalement d'origine : on vient de le
      // traiter, la suite du travail est sur l'intervention, pas sur lui.
      selectSection('interventions');
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Echec de l'ouverture de l'intervention.",
      );
      setOpenModal('echec');
    } finally {
      setWorking(false);
    }
  }, [
    openTicket,
    sdk,
    config.orgId,
    markOrderRead,
    reloadTickets,
    reloadOrders,
    selectSection,
  ]);

  /** Cloturer l'intervention ouverte : statut `termine` et date de cloture. */
  const onCloseOrder = useCallback(async (): Promise<void> => {
    if (openOrder == null) return;
    setWorking(true);
    try {
      await sdk.workOrders.update(openOrder.id, { status: 'termine', completedAt: new Date() });
      reloadOrders();
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Echec de la cloture de l'intervention.",
      );
      setOpenModal('echec');
    } finally {
      setWorking(false);
    }
  }, [openOrder, sdk, reloadOrders]);

  /* ---- Zone 6 : les actions de la SECTION active ---- */

  const ticketsBusy = tickets.status.loading || tickets.status.error != null;
  const ordersBusy = orders.status.loading || orders.status.error != null;

  // Contrat v2 : l'action d'ajout de chaque section se declare `kind: 'add'`,
  // le gabarit impose le libelle universel "Ajouter", IconPlus et le lime (G5).
  const quickActions = useMemo<ScreenAction[]>(() => {
    if (section === 'signalements') {
      return [
        {
          id: 'new-ticket',
          label: 'Ajouter',
          kind: 'add',
          disabled: ticketsBusy,
          onClick: openTicketForm,
        },
        {
          id: 'open-intervention',
          label: "Ouvrir l'intervention",
          // Sans signalement ouvert (la fiche) ou sans materiel dessus, il n'y
          // a rien a ouvrir : le bouton reste grise plutot que sans effet.
          disabled: openTicket == null || openTicketAssetId == null || working,
          onClick: () => void onOpenIntervention(),
        },
      ];
    }
    return [
      {
        id: 'new-intervention',
        label: 'Ajouter',
        kind: 'add',
        disabled: ordersBusy,
        onClick: openOrderForm,
      },
      {
        id: 'close-order',
        label: 'Cloturer',
        disabled: openOrder == null || openOrder.status === 'termine' || working,
        onClick: () => void onCloseOrder(),
      },
    ];
  }, [
    section,
    ticketsBusy,
    ordersBusy,
    openTicketForm,
    openOrderForm,
    openTicket,
    openTicketAssetId,
    openOrder,
    working,
    onOpenIntervention,
    onCloseOrder,
    tickets.status.loading,
    orders.status.loading,
    reloadTickets,
    reloadOrders,
  ]);

  const active = section === 'signalements' ? tickets : orders;

  /* ---- Les lignes de pieces du formulaire d'intervention ---- */

  function updatePart(index: number, patch: Partial<PartDraft>): void {
    setOrderParts((current) =>
      current.map((part, position) => (position === index ? { ...part, ...patch } : part)),
    );
  }

  function addPart(): void {
    setOrderParts((current) => [...current, { ...EMPTY_PART }]);
  }

  function removePart(index: number): void {
    setOrderParts((current) =>
      current.length <= 1 ? [{ ...EMPTY_PART }] : current.filter((_, position) => position !== index),
    );
  }

  const draftPartsTotal = orderParts.reduce(
    (sum, part) => sum + (Number(part.qty) || 0) * (Number(part.unitPrice) || 0),
    0,
  );
  const draftTotal =
    draftPartsTotal + ((Math.max(0, Number(orderLabor) || 0) / 60) * laborRate);

  return (
    <>
      <ScreenLayout
        /* Zone 1 : identite de l'app et menu des deux sections. La section
           active vient du socle (donc de l'adresse), et les compteurs portent
           le jeu COMPLET de chaque collection, jamais la section courante :
           c'est pour eux que les deux chargeurs ignorent `query.section`. */
        appName="Maintenance"
        appIcon={<IconScrewdriverWrench size={18} />}
        menuItems={[
          { id: 'signalements', label: 'Signalements', count: liveTickets.length },
          { id: 'interventions', label: 'Interventions', count: liveOrders.length },
        ]}
        activeMenuId={section}
        onMenuSelect={(id) => selectSection(toSectionId(id))}
        /* Zone 2 : le h1 affiche le label de la section active ; `title` est le repli. */
        title="Maintenance"
        /* Zone 3 : les stats de la section active, choisies dans les reglages,
           toujours calculees des donnees reelles. */
        headerStats={headerStats}
        /* Zone 4, groupe defilant : recherche et pastilles de la section active. */
        onSearchChange={active.setSearch}
        searchValue={active.query.search}
        searchPlaceholder={
          section === 'signalements' ? 'Rechercher un signalement' : 'Rechercher une intervention'
        }
        filters={active.filters}
        activeFilterId={active.activeFilterId}
        onFilterSelect={active.setFilter}
        /* Zone 4, groupe epingle : le selecteur de vues de la section active. */
        views={active.views}
        activeViewId={active.viewId}
        onViewSelect={active.setView}
        viewActions={active.viewActions}
        /* Zone 4, groupe de GAUCHE au niveau fiche : la puce de retour (qui
           porte aussi `Echap`) puis l'indicateur d'enregistrement. Passees SANS
           condition : le gabarit ne les rend qu'au niveau `record`, et une fiche
           sans elles serait une nasse (la recherche s'efface, aucune sortie
           n'apparait). Elles suivent la section ACTIVE, comme le reste. */
        onRecordExit={() => active.openRecord(null)}
        recordExitLabel={active.views.find((view) => view.id === active.lastCollectionViewId)?.label}
        saveState={active.saveState}
        onRetrySave={active.retrySave}
        /* Groupe EPINGLE au niveau fiche : le curseur prend la place du
           selecteur de vues, meme raison, meme absence de condition. */
        recordCursor={active.cursor}
        onRecordNavigate={active.openRecord}
        scrollRef={active.scrollRef}
        /* Zone 6 : les actions de la section active, un seul bouton lime. */
        quickActions={quickActions}
      >
        {/* Zone 5 pilotee par une VUE. Le ViewHost tient les etats standard
            (erreur, chargement, fiche introuvable, vide) : l'ecran ne les passe
            donc PAS au gabarit. On ne monte QUE la section active : les deux
            etats survivent, chacun garde sa vue et sa recherche. */}
        {section === 'signalements' ? (
          <ViewHost
            // La cle force un hote par section : sans elle React reconcilie les
            // deux et la memoire de defilement d'une section s'appliquerait a
            // l'autre.
            key="signalements"
            state={tickets}
            errorTitle="Echec du chargement des signalements"
            retryLabel="Reessayer"
            emptyTitle="Aucun signalement pour l'instant"
            emptyHint={'Clique sur "Ajouter", ou importe le jeu de demonstration dans les Parametres.'}
            notFoundTitle="Signalement introuvable"
            notFoundHint="Ce signalement a ete supprime, ou tu n'y as pas acces."
          />
        ) : (
          <ViewHost
            key="interventions"
            state={orders}
            errorTitle="Echec du chargement des interventions"
            retryLabel="Reessayer"
            emptyTitle="Aucune intervention pour l'instant"
            emptyHint={'Clique sur "Ajouter", ou ouvre une intervention depuis un signalement.'}
            notFoundTitle="Intervention introuvable"
            notFoundHint="Cette intervention a ete supprimee, ou tu n'y as pas acces."
          />
        )}
      </ScreenLayout>

      {/* --------- Formulaire : nouveau signalement --------- */}
      <Modal
        open={openModal === 'ticket'}
        onClose={closeModal}
        title="Nouveau signalement"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={submitting}>
              Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="maintenance-ticket-form"
              loading={submitting}
              disabled={parc.length === 0}
            >
              Creer le signalement
            </Button>
          </>
        }
      >
        <form id="maintenance-ticket-form" onSubmit={onCreateTicket} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Materiel concerne</span>
            <select
              className={FIELD_CLASS}
              value={ticketAsset}
              onChange={(event) => setTicketAsset(event.target.value)}
              required
            >
              {parc.length === 0 ? (
                <option value="">Aucun materiel dans le parc</option>
              ) : (
                parc.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.name}
                  </option>
                ))
              )}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Titre du signalement</span>
            <Input
              value={ticketLabel}
              onChange={(event) => setTicketLabel(event.target.value)}
              placeholder="Bague de mise au point dure"
              required
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Description</span>
            <textarea
              className={TEXTAREA_CLASS}
              value={ticketDescription}
              onChange={(event) => setTicketDescription(event.target.value)}
              placeholder="Ce qui a ete constate, dans quelles circonstances."
            />
          </label>

          <div className="flex gap-3">
            <label className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className={LABEL_CLASS}>Cause</span>
              <select
                className={FIELD_CLASS}
                value={ticketCause}
                onChange={(event) => setTicketCause(event.target.value)}
              >
                {CAUSE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className={LABEL_CLASS}>Priorite</span>
              <select
                className={FIELD_CLASS}
                value={ticketPriority}
                onChange={(event) => setTicketPriority(event.target.value)}
              >
                {TICKET_PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex gap-3">
            <label className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className={LABEL_CLASS}>Canal</span>
              <select
                className={FIELD_CLASS}
                value={ticketChannel}
                onChange={(event) => setTicketChannel(event.target.value)}
              >
                {CHANNEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className={LABEL_CLASS}>Echeance de remise en service</span>
              <Input
                type="date"
                value={ticketDue}
                onChange={(event) => setTicketDue(event.target.value)}
              />
            </label>
          </div>

          {/* La location d'origine : PENDANT QUELLE location le materiel a ete
              casse. Une liste deroulante, jamais un identifiant a taper : c'est
              cette ligne qui mene du materiel casse au client a refacturer.
              Les locations sont proposees les plus RECENTES d'abord, et
              l'entree vide reste en tete (tout n'arrive pas en location). */}
          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Location d'origine</span>
            <select
              className={FIELD_CLASS}
              value={ticketBooking}
              onChange={(event) => setTicketBooking(event.target.value)}
            >
              <option value="">Aucune location</option>
              {locations.map((booking) => (
                <option key={booking.id} value={booking.id}>
                  {bookingOptionLabel(booking)}
                </option>
              ))}
            </select>
          </label>

          {/* Le planning est charge et vide : on le dit, au lieu de laisser
              croire que la liste n'a pas fini d'arriver. Tant que le premier
              chargement tourne, rien ne s'affiche ici. */}
          {referencesLoaded && locations.length === 0 ? (
            <p className="text-xs text-ink-400">
              Aucune location au planning : importe le jeu de l'app Reservations pour tracer la
              location d'origine et le client a refacturer.
            </p>
          ) : null}

          {ticketCause === CAUSE_CLIENT && ticketBooking === '' ? (
            <p className="text-xs text-ink-400">
              Une casse imputable au client se rattache a sa location d'origine : c'est elle qui
              designe le client a refacturer.
            </p>
          ) : null}

          {formError != null ? (
            <p className="text-sm text-danger" role="alert">
              {formError}
            </p>
          ) : null}
        </form>
      </Modal>

      {/* --------- Formulaire : nouvelle intervention --------- */}
      <Modal
        open={openModal === 'intervention'}
        onClose={closeModal}
        title="Nouvelle intervention"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={submitting}>
              Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="maintenance-order-form"
              loading={submitting}
              disabled={parc.length === 0}
            >
              Creer l'intervention
            </Button>
          </>
        }
      >
        <form id="maintenance-order-form" onSubmit={onCreateOrder} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Materiel</span>
            <select
              className={FIELD_CLASS}
              value={orderAsset}
              onChange={(event) => setOrderAsset(event.target.value)}
              required
            >
              {parc.length === 0 ? (
                <option value="">Aucun materiel dans le parc</option>
              ) : (
                parc.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.name}
                  </option>
                ))
              )}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Diagnostic</span>
            <textarea
              className={TEXTAREA_CLASS}
              value={orderDiagnosis}
              onChange={(event) => setOrderDiagnosis(event.target.value)}
              placeholder="Ce qui est casse, et ce qu'on fait pour le reparer."
            />
          </label>

          <div className="flex flex-col gap-2">
            <span className={LABEL_CLASS}>Lignes de pieces</span>
            {orderParts.map((part, index) => (
              <div key={`part-${String(index)}`} className="flex items-center gap-2">
                <Input
                  aria-label={`Designation de la piece ${String(index + 1)}`}
                  className="min-w-0 flex-1"
                  value={part.label}
                  onChange={(event) => updatePart(index, { label: event.target.value })}
                  placeholder="Designation de la piece"
                />
                <Input
                  aria-label={`Quantite de la piece ${String(index + 1)}`}
                  className="w-[4.5rem] shrink-0"
                  type="number"
                  min="0"
                  value={part.qty}
                  onChange={(event) => updatePart(index, { qty: event.target.value })}
                />
                <Input
                  aria-label={`Prix unitaire de la piece ${String(index + 1)}`}
                  className="w-[7rem] shrink-0"
                  type="number"
                  min="0"
                  value={part.unitPrice}
                  onChange={(event) => updatePart(index, { unitPrice: event.target.value })}
                  placeholder="EUR"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => removePart(index)}
                  aria-label={`Retirer la ligne ${String(index + 1)}`}
                >
                  Retirer
                </Button>
              </div>
            ))}
            <div>
              <Button variant="secondary" size="sm" type="button" onClick={addPart}>
                Ajouter une ligne
              </Button>
            </div>
          </div>

          <div className="flex gap-3">
            <label className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className={LABEL_CLASS}>Main d'oeuvre (minutes)</span>
              <Input
                type="number"
                min="0"
                value={orderLabor}
                onChange={(event) => setOrderLabor(event.target.value)}
              />
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className={LABEL_CLASS}>Date planifiee</span>
              <Input
                type="date"
                value={orderPlanned}
                onChange={(event) => setOrderPlanned(event.target.value)}
              />
            </label>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className={LABEL_CLASS}>Imputation</span>
              <select
                className={FIELD_CLASS}
                value={orderChargeTo}
                onChange={(event) => setOrderChargeTo(event.target.value)}
              >
                {CHARGE_TO_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex shrink-0 items-center gap-2 self-end pb-4">
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 cursor-pointer"
                checked={orderBillable}
                onChange={(event) => setOrderBillable(event.target.checked)}
              />
              <span className="text-sm text-ink-900">Refacturable</span>
            </label>
          </div>

          <p className="text-xs text-ink-500">
            {`Cout estime : ${formatEuros(draftPartsTotal)} de pieces, ${formatEuros(
              draftTotal - draftPartsTotal,
            )} de main d'oeuvre au taux de ${formatEuros(laborRate)} par heure, soit ${formatEuros(
              draftTotal,
            )}.`}
          </p>

          {clients.length === 0 ? (
            <p className="text-xs text-ink-400">
              Aucun client au carnet : la refacturation restera sans destinataire tant que le jeu de
              l'app Reservations n'est pas importe.
            </p>
          ) : null}

          {formError != null ? (
            <p className="text-sm text-danger" role="alert">
              {formError}
            </p>
          ) : null}
        </form>
      </Modal>

      {/* --------- Compte rendu d'echec d'une action de la zone 6 --------- */}
      <Modal
        open={openModal === 'echec'}
        onClose={closeModal}
        title="Action impossible"
        size="sm"
        footer={
          <Button variant="secondary" onClick={closeModal}>
            Fermer
          </Button>
        }
      >
        <p className="text-sm text-ink-900" role="alert">
          {actionError ?? "L'ecriture n'a pas abouti."}
        </p>
      </Modal>
    </>
  );
}

export default MaintenanceScreen;
